import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Barbell, Users, Link as LinkIcon, Copy, CheckCircle, X, Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const SPECIALTIES = [
  "Strength & Conditioning",
  "Powerlifting",
  "Bodybuilding",
  "Hypertrophy",
  "Weight Loss",
  "Athletic Performance",
  "Crossfit / Functional",
  "General Fitness",
  "Rehab / Return-to-Sport",
];

export default function CoachOnboardingWizard({ open, onComplete }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    coach_bio: "",
    coach_specialty: "",
    coach_mode_default: "1on1",
  });
  const [inviteCode, setInviteCode] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(0);
      setInviteCode(null);
      setCopied(false);
    }
  }, [open]);

  const inviteUrl = inviteCode
    ? `${window.location.origin}/join/${inviteCode}`
    : "";

  const savedProfile = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/coach/onboarding/complete", form);
      setUser(data.user);
      setStep(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save profile");
    } finally {
      setLoading(false);
    }
  };

  const createInvite = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/coach/invites", { mode: form.coach_mode_default });
      setInviteCode(data.code);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create invite link");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  const skip = async () => {
    setLoading(true);
    try {
      await api.post("/coach/onboarding/dismiss");
      const { data: me } = await api.get("/auth/me");
      setUser(me);
      toast.success("You can complete this later from your profile.");
      onComplete?.();
    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const finish = () => {
    toast.success("Coach setup complete. Let's build your roster.");
    onComplete?.();
    navigate("/coach/clients");
  };

  const progressPct = ((step + 1) / 2) * 100;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !loading) onComplete?.(); }}>
      <DialogContent
        className="bg-surface-base border-white/10 text-white max-w-2xl p-0 overflow-hidden [&>button]:hidden"
        data-testid="coach-onboarding-wizard"
      >
        <DialogTitle className="sr-only">Edge Training Coach Onboarding</DialogTitle>
        <DialogDescription className="sr-only">
          Set up your coach profile and invite your first athlete in two quick steps.
        </DialogDescription>

        {/* Skip / close */}
        <div className="absolute top-3 right-3 z-10">
          <button
            onClick={skip}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] label-tiny tracking-widest text-text-secondary hover:text-white border border-white/10 hover:border-white/40 transition-colors disabled:opacity-50"
            data-testid="coach-onboarding-skip-button"
            aria-label="Skip coach onboarding"
          >
            <X size={12} weight="bold" /> SKIP FOR NOW
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-white/5">
          <div
            className="h-full bg-brand-accent transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        <div className="p-8 pt-10">
          <div className="label-tiny text-brand-accent mb-1">
            // COACH SETUP · STEP {step + 1} OF 2
          </div>

          {step === 0 && (
            <div data-testid="coach-onboarding-step-profile">
              <h2 className="font-heading text-3xl mb-2">SET UP YOUR COACH PROFILE</h2>
              <p className="text-text-secondary text-sm mb-6">
                Athletes will see this on their invite page and coach card.
              </p>

              {/* Specialty */}
              <div className="mb-5">
                <label className="label-tiny block mb-2">SPECIALTY</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {SPECIALTIES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, coach_specialty: s }))}
                      className={`text-left text-xs px-3 py-2 border transition-colors ${
                        form.coach_specialty === s
                          ? "border-brand-accent text-brand-accent bg-brand-accent/5"
                          : "border-white/10 text-text-secondary hover:border-white/40 hover:text-white"
                      }`}
                      data-testid={`specialty-${s.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Bio */}
              <div className="mb-5">
                <label className="label-tiny block mb-2">SHORT BIO (60 CHAR MAX)</label>
                <input
                  type="text"
                  maxLength={60}
                  value={form.coach_bio}
                  onChange={(e) => setForm((f) => ({ ...f, coach_bio: e.target.value }))}
                  placeholder="e.g. USAPL national coach. Built 40+ raw totals over 1500lb."
                  className="w-full bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
                  data-testid="coach-bio-input"
                />
                <div className="label-tiny text-text-secondary/60 mt-1 text-right tabular-nums">
                  {form.coach_bio.length}/60
                </div>
              </div>

              {/* Mode */}
              <div className="mb-8">
                <label className="label-tiny block mb-2">DEFAULT COACHING MODE</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { k: "1on1", label: "1-ON-1", hint: "Individual clients" },
                    { k: "team", label: "TEAM", hint: "Groups / squads (coming soon)" },
                  ].map((m) => (
                    <button
                      key={m.k}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, coach_mode_default: m.k }))}
                      className={`text-left px-4 py-3 border transition-colors ${
                        form.coach_mode_default === m.k
                          ? "border-brand-accent text-brand-accent bg-brand-accent/5"
                          : "border-white/10 text-text-secondary hover:border-white/40 hover:text-white"
                      }`}
                      data-testid={`mode-${m.k}`}
                    >
                      <div className="text-sm font-heading">{m.label}</div>
                      <div className="label-tiny mt-1 text-[10px] text-text-secondary">{m.hint}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={savedProfile}
                  disabled={loading || !form.coach_specialty}
                  className="btn-kinetic px-6 py-2.5 text-xs label-tiny tracking-widest disabled:opacity-40 flex items-center gap-2"
                  data-testid="coach-onboarding-next-button"
                >
                  {loading ? "SAVING..." : "CONTINUE"} <ArrowRight size={12} weight="bold" />
                </button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="coach-onboarding-step-invite">
              <h2 className="font-heading text-3xl mb-2">INVITE YOUR FIRST ATHLETE</h2>
              <p className="text-text-secondary text-sm mb-6">
                Generate a shareable link and send it to your athlete via text, email, or DM.
              </p>

              {!inviteCode ? (
                <div className="tactical-card p-8 text-center mb-6">
                  <LinkIcon size={40} weight="duotone" className="text-brand-accent mx-auto mb-3" />
                  <p className="text-sm text-text-secondary mb-5">
                    We&apos;ll create a private, 14-day invite link only your athlete can use.
                  </p>
                  <button
                    onClick={createInvite}
                    disabled={loading}
                    className="btn-kinetic px-6 py-2.5 text-xs label-tiny tracking-widest disabled:opacity-40"
                    data-testid="generate-first-invite-button"
                  >
                    {loading ? "GENERATING..." : "GENERATE INVITE LINK"}
                  </button>
                </div>
              ) : (
                <div className="mb-6">
                  <label className="label-tiny block mb-2">YOUR INVITE LINK</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={inviteUrl}
                      readOnly
                      className="flex-1 bg-surface-elevated border border-white/10 px-3 py-2 text-xs font-mono"
                      data-testid="invite-link-input"
                      onFocus={(e) => e.target.select()}
                    />
                    <button
                      onClick={copyLink}
                      className="btn-kinetic px-4 py-2 text-xs label-tiny flex items-center gap-2"
                      data-testid="invite-copy-button"
                    >
                      {copied ? <CheckCircle size={12} weight="fill" /> : <Copy size={12} weight="bold" />}
                      {copied ? "COPIED" : "COPY"}
                    </button>
                  </div>
                  <div className="mt-3 p-3 border border-white/10 bg-surface-elevated text-xs text-text-secondary">
                    <div className="label-tiny text-brand-accent mb-1">// SUGGESTED MESSAGE</div>
                    Hey — I&apos;d love to coach you on Edge Training. Sign up with this private link and I&apos;ll see your workouts and program you in-app: <span className="text-white break-all">{inviteUrl}</span>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={() => setStep(0)}
                  disabled={loading}
                  className="text-xs label-tiny text-text-secondary hover:text-white transition-colors flex items-center gap-1 disabled:opacity-40"
                  data-testid="coach-onboarding-back-button"
                >
                  <ArrowLeft size={12} /> BACK
                </button>
                <button
                  onClick={finish}
                  className="btn-kinetic px-6 py-2.5 text-xs label-tiny tracking-widest flex items-center gap-2"
                  data-testid="coach-onboarding-finish-button"
                >
                  <Sparkle size={12} weight="fill" /> ENTER COACH HUB
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
